import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  LinearProgress,
  Button,
  IconButton,
  Alert,
  Chip,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { documentsApi } from "../services/rag-service";
import { useTranslation } from "react-i18next";

/**
 * Component to display the progress of a document upload task
 * Retries 5 times if initial fetch fails, then polls every 20 seconds
 * Completely stops all API calls when dialog is closed
 */
const TaskProgressDialog = ({ taskId, collectionId, open, onClose }) => {
  const { t, i18n } = useTranslation();
  const [taskStatus, setTaskStatus] = useState(null);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [fetchSuccessful, setFetchSuccessful] = useState(false);

  // Use refs to track intervals/timeouts to ensure cleanup
  const pollingIntervalRef = useRef(null);
  const retryTimerRef = useRef(null);
  const isDialogOpenRef = useRef(open);

  const formatTaskDate = useCallback(
    (value) => {
      if (!value) return "";
      return new Date(value).toLocaleString(i18n.language || "en");
    },
    [i18n.language],
  );

  // Function to clear all timers in one place
  const clearAllTimers = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // Update ref when open state changes
  useEffect(() => {
    isDialogOpenRef.current = open;

    // When dialog is closed, immediately clear all timers
    if (!open) {
      clearAllTimers();
    }
  }, [open, clearAllTimers]);

  // Fetch task status from API with safety check
  const fetchTaskStatus = useCallback(async () => {
    // Skip if dialog is closed or no taskId
    if (!isDialogOpenRef.current || !taskId) {
      return;
    }

    try {
      const status = await documentsApi.getTaskStatus(taskId);

      // Double check dialog is still open before updating state
      if (!isDialogOpenRef.current) return;

      setTaskStatus(status);
      setError(null);
      setFetchSuccessful(true);
      setRetryCount(0); // Reset retry count on success

      // Stop polling if task is completed or failed
      if (status.status === "completed" || status.status === "failed") {
        clearAllTimers();
      }
    } catch (err) {
      console.error("Error fetching task status:", err);

      // Double check dialog is still open before updating state
      if (!isDialogOpenRef.current) return;

      setError(err.message || t("common.error"));

      // Only increment retry count during initial retry phase
      if (!fetchSuccessful) {
        setRetryCount((prev) => prev + 1);
      }
    } finally {
      // Double check dialog is still open before updating state
      if (isDialogOpenRef.current) {
        // setIsLoading(false); - this line is removed
      }
    }
  }, [taskId, clearAllTimers, fetchSuccessful, t]);

  // Initial fetch with retry logic
  useEffect(() => {
    if (!taskId || !open) return;

    // Clear any existing timers first
    clearAllTimers();

    // If we haven't had a successful fetch yet and we're under retry limit
    if (!fetchSuccessful && retryCount < 5) {
      // Make initial fetch
      fetchTaskStatus();

      // Set up retry timer if needed (only if previous attempt failed)
      if (retryCount > 0) {
        const timer = setTimeout(() => {
          // Only proceed if dialog is still open
          if (isDialogOpenRef.current && retryCount < 5 && !fetchSuccessful) {
            fetchTaskStatus();
          }
        }, 5000);

        retryTimerRef.current = timer;
      }
    }

    // Clear retry timer on cleanup
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [
    taskId,
    open,
    fetchTaskStatus,
    retryCount,
    fetchSuccessful,
    clearAllTimers,
  ]);

  // Set up regular polling after successful fetch
  useEffect(() => {
    if (!taskId || !open || !fetchSuccessful) return;

    // Clear any existing polling
    clearAllTimers();

    // Start polling every 20 seconds after successful fetch
    const intervalId = setInterval(() => {
      // Only make the API call if dialog is still open
      if (isDialogOpenRef.current) {
        fetchTaskStatus();
      } else {
        // Safety check: clear interval if dialog somehow closed
        clearInterval(intervalId);
      }
    }, 20000);

    pollingIntervalRef.current = intervalId;

    // Cleanup on unmount or when dialog closes
    return () => {
      clearInterval(intervalId);
      pollingIntervalRef.current = null;
    };
  }, [taskId, open, fetchTaskStatus, fetchSuccessful, clearAllTimers]);

  // Ensure complete cleanup when component unmounts
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  // Get appropriate color based on task status
  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "running":
      case "pending":
        return "primary";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (statusData) => {
    return statusData?.status_label || statusData?.status || "";
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon color="success" />;
      case "running":
      case "pending":
        return <InfoIcon color="primary" />;
      case "failed":
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="warning" />;
    }
  };

  // Custom close handler to ensure timers are cleared
  const handleClose = () => {
    clearAllTimers();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "300px",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>
        {t("taskProgress.documentImportProgress")}
        <IconButton
          aria-label={t("common.close")}
          onClick={handleClose}
          sx={{ position: "absolute", insetInlineEnd: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        {error && retryCount >= 5 ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("taskProgress.fetchFailed", { error })}
          </Alert>
        ) : !taskStatus ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1">
              {!fetchSuccessful
                ? t("taskProgress.fetchAttempts", {
                    attempt: retryCount + 1,
                  })
                : t("taskProgress.loadingStatus")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <Box
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                {t("taskProgress.task", {
                  id: `${taskStatus.task_id.substring(0, 8)}...`,
                })}
              </Typography>
              <Chip
                label={getStatusLabel(taskStatus)}
                color={getStatusColor(taskStatus.status)}
                icon={getStatusIcon(taskStatus.status)}
              />
            </Box>

            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                {taskStatus.message}
              </Typography>

              <Box sx={{ mt: 3, mb: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">
                    {t("taskProgress.overallProgress")}
                  </Typography>
                  <Typography variant="body2">
                    {taskStatus.completion_percentage}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={taskStatus.completion_percentage || 0}
                  sx={{ height: 10, borderRadius: 5, mt: 1 }}
                />
              </Box>

              <Box
                sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}
              >
                <Typography variant="body2">
                  {t("taskProgress.filesProcessed", {
                    processed: taskStatus.processed_files || 0,
                    total: taskStatus.total_files || 0,
                  })}
                </Typography>
                {taskStatus.failed_files > 0 && (
                  <Typography variant="body2" color="error.main">
                    {t("taskProgress.filesFailed", {
                      count: taskStatus.failed_files,
                    })}
                  </Typography>
                )}
              </Box>
            </Paper>

            <Box sx={{ mt: "auto" }}>
                <Typography variant="caption" color="text.secondary">
                {t("taskProgress.started", {
                  date: formatTaskDate(taskStatus.started_at),
                })}
              </Typography>
              {taskStatus.updated_at && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  {t("taskProgress.lastUpdated", {
                    date: formatTaskDate(taskStatus.updated_at),
                  })}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", fontStyle: "italic" }}
              >
                {t("taskProgress.refreshing")}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Alert severity="info" sx={{ flexGrow: 1 }} icon={false}>
          {t("taskProgress.backgroundNotice")}
        </Alert>
        <Button onClick={handleClose}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskProgressDialog;
